import { useState, useEffect } from 'react';
import { useStaticCollections } from '../../context/StaticCollections.jsx';
import { useLoggedInUserContextProvider } from '../../context/LoggedInUserContext.jsx';
import { useAdminCollections } from '../../context/AdminCollections.jsx';
import { axiosPost } from '../../utils/axiosPost.js';
import { axiosPut } from '../../utils/axiosPut.js';
import { axiosDelete } from '../../utils/axiosDelete.js';
import { getNominationRoundYear } from '../../utils/getNominationRoundYear.js';
import { NOTIFICATION_DISPLAY_MS } from '../../utils/constants.js';
import { devError } from '../../utils/devLog';

export const useCOI = (
  selectedCandidate,
  onCandidateSelect,
  setNotificationTitle,
  setNotificationMessage,
) => {
  const [showCOIModal, setShowCOIModal] = useState(false);
  const [coiType, setCOIType] = useState([]);
  const [coiDetail, setCOIDetail] = useState('');
  const [coiValidationError, setCOIValidationError] = useState('');
  const [existingDeclaration, setExistingDeclaration] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const { coiTypesArray } = useStaticCollections();
  const { userDetails } = useLoggedInUserContextProvider();
  const {
    triggerCOIRefresh,
    coiDeclarationArray,
    candidateScoresArray,
    setCandidateScoresArray,
    setCOIUpdateInProgress,
  } = useAdminCollections();

  const coiTypeOptions =
    coiTypesArray?.map((item) => ({
      value: item.attributes.value,
      label: item.attributes.name,
      category: item.attributes.category,
      id: item.id,
    })) || [];

  const checkExistingDeclaration = async () => {
    if (!selectedCandidate || !userDetails?.id) return null;

    try {
      const currentRound = getNominationRoundYear(new Date());
      const isAdmin =
        userDetails?.roles?.includes('super-admin') ||
        userDetails?.roles?.includes('admin');
      const isSCChair = userDetails?.roles?.includes('committee-chair');
      const isSCMember = userDetails?.roles?.includes('committee-member');
      const isCouncilMember = userDetails?.roles?.includes('council-member');

      let filteredDeclarations = coiDeclarationArray.filter(
        (declaration) =>
          declaration.attributes.candidateId ===
            selectedCandidate.candidateId &&
          declaration.attributes.nominationRound === currentRound &&
          declaration.attributes.nominationId ===
            selectedCandidate.nominationId,
      );

      if (!isAdmin && (isSCChair || isSCMember || isCouncilMember)) {
        filteredDeclarations = filteredDeclarations.filter(
          (declaration) => declaration.attributes.userId === userDetails.id,
        );
      }

      return filteredDeclarations?.[0] || null;
    } catch (error) {
      devError('Error checking existing declaration:', error);
      return null;
    }
  };

  const loadExistingDeclaration = async (declaration) => {
    if (!declaration) return;

    const existingCoiTypes =
      declaration.attributes.coiTypes?.data?.map(
        (type) => type.attributes.value,
      ) || [];

    setCOIType(existingCoiTypes);
    setCOIDetail(declaration.attributes.coiDetail || '');
    setIsUpdating(true);
  };

  useEffect(() => {
    if (selectedCandidate) {
      setCOIType([]);
      setCOIDetail('');
      setCOIValidationError('');
      setExistingDeclaration(null);
      setIsUpdating(false);
      setShowCOIModal(true);

      checkExistingDeclaration().then((existing) => {
        if (existing) {
          setExistingDeclaration(existing);
          loadExistingDeclaration(existing);
          setCOIValidationError(
            'You have already submitted a COI declaration for this candidate. You can still choose to update your declaration.',
          );
        }
      });
    } else {
      setShowCOIModal(false);
    }
  }, [selectedCandidate]);

  const handleCOITypeChange = (selectedType) => {
    const option = coiTypeOptions.find((opt) => opt.value === selectedType);
    if (!option) return;

    const getCurrentCategory = () => {
      if (coiType.length === 0) return null;
      const firstType = coiTypeOptions.find((opt) => opt.value === coiType[0]);
      return firstType?.category || null;
    };

    const currentCategory = getCurrentCategory();
    const newCategory = option.category;

    const shouldClearDetail =
      newCategory === 'none' ||
      (currentCategory && currentCategory !== newCategory);

    if (shouldClearDetail) {
      setCOIDetail('');
    }

    if (newCategory === 'none') {
      setCOIType(['no-conflicts']);
    } else if (newCategory === 'direct') {
      const newTypes = coiType.filter((type) => {
        const existingOption = coiTypeOptions.find((opt) => opt.value === type);
        return existingOption?.category === 'direct';
      });

      if (!newTypes.includes(selectedType)) {
        newTypes.push(selectedType);
      }
      setCOIType(newTypes);
    } else if (newCategory === 'indirect') {
      const newTypes = coiType.filter((type) => {
        const existingOption = coiTypeOptions.find((opt) => opt.value === type);
        return existingOption?.category === 'indirect';
      });

      if (!newTypes.includes(selectedType)) {
        newTypes.push(selectedType);
      }
      setCOIType(newTypes);
    }
  };

  const handleCOITypeRemove = (typeToRemove) => {
    const newTypes = coiType.filter((type) => type !== typeToRemove);
    setCOIType(newTypes);

    if (newTypes.length === 0) {
      setCOIDetail('');
    }
  };

  const handleCOISave = async () => {
    setCOIValidationError('');

    if (coiDetail.length > 300) {
      setCOIValidationError('COI detail must be 300 characters or less.');
      return;
    }

    try {
      const selectedCoiTypeIds = coiType
        .map((typeValue) => {
          const foundType = coiTypeOptions.find(
            (option) => option.value === typeValue,
          );
          return foundType?.id;
        })
        .filter(Boolean);

      const payload = {
        data: {
          nominationId: selectedCandidate.nominationId,
          candidateId: selectedCandidate.candidateId,
          userId: userDetails?.id,
          nominationRound: getNominationRoundYear(new Date()),
          coiTypes: selectedCoiTypeIds,
          coiDetail: coiDetail.trim() || null,
          sectionalCommitteeId: selectedCandidate.sectionalCommitteeId,
          isCompleted: true,
        },
      };

      let response;
      let success = false;

      if (isUpdating && existingDeclaration) {
        response = await axiosPut(
          `coi-declarations/${existingDeclaration.id}`,
          payload,
        );

        if (response.status === 200) {
          success = true;
          triggerCOIRefresh(response?.data?.data?.id);

          const hasDirectConflicts = coiType.some((typeValue) => {
            const option = coiTypeOptions.find(
              (opt) => opt.value === typeValue,
            );
            return option?.category === 'direct';
          });

          if (hasDirectConflicts) {
            try {
              setCOIUpdateInProgress(true);

              const existingScores = candidateScoresArray?.filter(
                (score) =>
                  score.attributes.candidateId ===
                    selectedCandidate.candidateId &&
                  score.attributes.userId === userDetails?.id,
              );

              if (existingScores?.length > 0) {
                const axiosDeleteResponse = await axiosDelete(
                  `candidate-scores/${existingScores?.[0]?.id}`,
                );

                if (axiosDeleteResponse?.status === 200) {
                  const updatedScores = candidateScoresArray.filter(
                    (score) => score.id !== existingScores[0].id,
                  );
                  setCandidateScoresArray(updatedScores);

                  setTimeout(
                    () => setCOIUpdateInProgress(false),
                    NOTIFICATION_DISPLAY_MS,
                  );
                } else {
                  setCOIUpdateInProgress(false);
                }
              } else {
                setCOIUpdateInProgress(false);
              }
            } catch (error) {
              devError('Error deleting existing scores:', error);
              setCOIUpdateInProgress(false);
            }
          }
        }
      } else {
        response = await axiosPost('coi-declarations', payload);

        if (response.status === 200) {
          success = true;
          triggerCOIRefresh(response?.data?.data?.id);
        }
      }

      if (success) {
        setNotificationTitle(
          isUpdating ? 'COI Declaration Updated' : 'COI Declaration Saved',
        );
        setNotificationMessage(
          `Your conflict of interest declaration for ${selectedCandidate.name} has been ${isUpdating ? 'successfully updated' : 'recorded successfully'}.`,
        );

        setTimeout(() => {
          setNotificationMessage('');
          setNotificationTitle('');
        }, NOTIFICATION_DISPLAY_MS);

        onCandidateSelect(null);
        setCOIType([]);
        setCOIDetail('');
        setExistingDeclaration(null);
        setIsUpdating(false);
      }
    } catch (error) {
      devError('Error saving COI:', error);
      setCOIValidationError(
        `Failed to ${isUpdating ? 'update' : 'save'} COI declaration. Please try again.`,
      );

      setNotificationTitle(isUpdating ? 'Update Failed' : 'Save Failed');
      setNotificationMessage(
        `Unable to ${isUpdating ? 'update' : 'save'} your COI declaration for ${selectedCandidate.name}. Please check your connection and try again.`,
      );

      setTimeout(() => {
        setNotificationMessage('');
        setNotificationTitle('');
      }, NOTIFICATION_DISPLAY_MS);

      setCOIUpdateInProgress(false);
    }
  };

  const handleCOICancel = () => {
    onCandidateSelect(null);
    setCOIType([]);
    setCOIDetail('');
    setCOIValidationError('');
    setExistingDeclaration(null);
    setIsUpdating(false);
  };

  return {
    showCOIModal,
    coiType,
    coiDetail,
    coiValidationError,
    coiTypeOptions,
    handleCOITypeChange,
    handleCOITypeRemove,
    handleCOISave,
    handleCOICancel,
    setCOIDetail,
    isUpdating,
  };
};
